/**
 * Hero Centered rules — the single validation point for a whole Hero Centered.
 *
 * A centered hero's eyebrow, title and description must each say something; its
 * title and description must also fit their schema-driven size ceilings. Empty
 * and wordless values share `trim()` and the same required-field message.
 *
 * **These rules are stated here rather than borrowed from {@link validateHero},
 * and the duplication is the design.** The two components are separate blocks so
 * that either may change without disturbing the other, and two blocks that share
 * one validator are not separate — the first divergence would be a refactor of a
 * green suite rather than an edit to one file. Stating them twice also keeps the
 * suite honest: a probe that removes a Hero rule must redden `compose-hero`
 * alone, and a probe here must redden `compose-hero-centered` alone. Were this a
 * delegation, one mutation would redden both and neither story would be saying
 * anything the other did not.
 *
 * The messages say "centered hero" for the same reason, and are what an Editor
 * reads. They name the block in front of them, not its sibling.
 *
 * Values are stored EXACTLY as entered, not trimmed. `trim()` decides whether a
 * value says anything; it does not get to edit what the Editor wrote.
 *
 * Check order is eyebrow → title → description, so an Editor who emptied two
 * fields at once is told about the first one rather than the last.
 *
 * Refusal is whole-payload atomic: the failing rule returns, nothing is mutated,
 * and no field is replaced.
 *
 * There is deliberately no rule about alignment. An Editor supplies none — see
 * the note in `blocks/hero-centered.ts`.
 */
import { HERO_CENTERED_MAX_LENGTHS } from "./blocks/hero-centered";
import { validateScalarSlotLength } from "./scalar-slot-validation";
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * One rule per field, in the order they are checked. Each entry is the whole
 * rule: a field of this name must be a string that says something.
 */
const FIELD_RULES = [
    ["eyebrow", "A centered hero needs an eyebrow", "Eyebrow", undefined],
    [
        "title",
        "A centered hero needs a title",
        "Heading",
        HERO_CENTERED_MAX_LENGTHS.heading,
    ],
    [
        "description",
        "A centered hero needs a description",
        "Subheading",
        HERO_CENTERED_MAX_LENGTHS.subheading,
    ],
];
/** Validate a whole Hero Centered. */
export function validateHeroCentered(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The centered hero is malformed" };
    }
    for (const [key, reason, label, maxLength] of FIELD_RULES) {
        const field = value[key];
        if (typeof field !== "string" || field.trim() === "") {
            return { valid: false, reason };
        }
        const length = validateScalarSlotLength({ label, maxLength }, field);
        if (!length.valid)
            return length;
    }
    return {
        valid: true,
        hero: {
            eyebrow: value.eyebrow,
            title: value.title,
            description: value.description,
        },
    };
}
//# sourceMappingURL=hero-centered.js.map